import React, { useState } from "react";
import { useEffect } from "react";

import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Alert,
  AlertTitle,
  Pagination,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import adminAxios from "../../adminApi/adminApi";
import { useAdminAuth } from "../../hooks/AdminAuthProvider";
import {
  GET_ALL_TENANTS_FOR_SUPER_ADMIN_DASHBOARD,
  GET_CAMPAIGN_TABLE_DATA,
  GET_CRON_JOBS_DATA,
  GET_DASHBOARD_TILES_COUNT,
} from "../../config/urls";
import MarketingCampaignTab from "./MarketingCampaignTab";
import ActiveUserSessionsTable from "./ActiveUserSessionsTable";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CronMonitoringDashboard from "./AdminCronUI/CronMonitoringDashboard";
import CustomPagination from "../../common/custom-pagination";

const AdminMarketingDashboard = () => {
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState(0); // 0=campaigns, 1=sessions, 2=tenants, 3=crons
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [dashboardTilesData, setDashboardTilesData] = useState({});
  const [campaignsData, setCampaignsData] = useState([]);
  const [cronJobsData, setCronJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAdminTokenValid, adminLogout } = useAdminAuth();
  const [planFilter, setPlanFilter] = useState("all");
  const [tenantPage, setTenantPage] = useState(0);
  const [tenantRowsPerPage, setTenantRowsPerPage] = useState(20);

  // Reset to the first page when filters change
  useEffect(() => {
    setTenantPage(0);
  }, [searchQuery, planFilter]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSearchQuery("");
  };

  useEffect(() => {
    if (!isAdminTokenValid()) {
      adminLogout();
      return;
    }

    fetchTenants();
    fetchDashboardTilesData();
    fetchCampaignData();
    fetchCronJobsData();
  }, [activeTab]);

  const statusDictionary = {
    success: "SUCCESS",
    error: "ERROR",
    scheduled: "SCHEDULED",
    in_progress: "IN_PROGRESS",
    created: "CREATED",
    cancelled: "CANCELLED",
  };

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(
        GET_ALL_TENANTS_FOR_SUPER_ADMIN_DASHBOARD
      );
      setTenants(response.data.tenants);
      setPlans(response.data.plans || []);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        // Already handled by interceptor
        return;
      }
      setError(err.response?.data?.detail || "Failed to fetch tenants");
      console.error("Error fetching tenants:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchCampaignData = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(GET_CAMPAIGN_TABLE_DATA);
      setCampaignsData(response.data.campaign_data);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        // Already handled by interceptor
        return;
      }
      setError(err.response?.data?.detail || "Failed to fetch tenants");
      console.error("Error fetching tenants:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchDashboardTilesData = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(GET_DASHBOARD_TILES_COUNT);
      setDashboardTilesData(response.data);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        // Already handled by interceptor
        return;
      }
      setError(err.response?.data?.detail || "Failed to fetch tenants");
      console.error("Error fetching tenants:", err);
    } finally {
      setLoading(false);
    }
  };
  const fetchCronJobsData = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(GET_CRON_JOBS_DATA);
      setCronJobsData(response.data.cron_logs);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        return;
      }
      setError(err.response?.data?.detail || "Failed to cron job logs");
      console.error("Error fetching cron job logs", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
  };
  


  function convertTimestamp(isoString) {
    if (!isoString) return "";

  const date = new Date(isoString);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // convert 0 → 12 for AM

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  }

  // Tenants arrive newest first from the API; filtering keeps that order
  const filteredTenants = tenants?.filter((tenant) => {
    const searchLower = searchQuery.trim().toLowerCase();
    const nameMatches =
      tenant?.name?.toLowerCase()?.includes(searchLower) || false;
    const matchesPlan =
      planFilter === "all" ||
      (tenant?.plan?.name || "none") === planFilter;
    return nameMatches && matchesPlan;
  });
  const formatBalance = (amount, currency) => {
    if (amount === null || amount === undefined) return "No wallet";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
      }).format(amount);
    } catch {
      return `${currency || ""} ${Number(amount).toFixed(2)}`.trim();
    }
  };
  const currentTenants = filteredTenants.slice(
    tenantPage * tenantRowsPerPage,
    (tenantPage + 1) * tenantRowsPerPage
  );

  const filteredCrons = cronJobsData.filter((cron) => {
    const searchLower = searchQuery.toLowerCase();
    const cronName = cron?.cron_name || "";
    const tenant = cron?.tenant || "";

    // Handle both string and array cases for tenant
    const tenantString = Array.isArray(tenant)
      ? tenant.join(", ")
      : tenant.toString();

    const matchesSearch =
      cronName.toString().toLowerCase().includes(searchLower) ||
      tenantString.toLowerCase().includes(searchLower);

    return matchesSearch;
  });
  const capitalize = (s) => {
    if (typeof s !== "string") return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };


  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      {/* Header */}
      <Paper elevation={1} sx={{ borderRadius: 0, mb: 3 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                component="h1"
                fontWeight="bold"
                gutterBottom
              >
                Master Console
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Centralized management for all tenant operations and campaigns
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Card sx={{ minWidth: 120 }}>
                <CardContent
                  sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}
                >
                  <Typography
                    variant="caption"
                    color="primary.main"
                    fontWeight="medium"
                  >
                    Total Tenants
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {dashboardTilesData?.total_tenants || 0}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 120 }}>
                <CardContent
                  sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}
                >
                  <Typography
                    variant="caption"
                    color="secondary.main"
                    fontWeight="medium"
                  >
                    Total Campaigns in progress
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="secondary.main"
                  >
                    {dashboardTilesData?.campaigns_count || 0}
                  </Typography>
                </CardContent>
              </Card>
              <Card sx={{ minWidth: 120 }}>
                <CardContent
                  sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}
                >
                  <Typography
                    variant="caption"
                    color="success.main"
                    fontWeight="medium"
                  >
                    Active Sessions
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {dashboardTilesData?.active_user_sessions || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                sx={{
                  marginRight: { sm: "6px", md: "8px" },
                  fontSize: { xs: "12px", sm: "14px", md: "16px", lg: "18px" },
                }}
              >
                Welcome
              </Typography>

              <Tooltip title="Logout">
                <IconButton aria-label="logout" onClick={handleLogout}>
                  <PowerSettingsNewIcon
                    sx={{
                      height: {
                        xs: "18px",
                        sm: "20px",
                        md: "24px",
                        lg: "28px",
                      },
                      width: "auto",
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth="xl">
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Campaigns
                  <Chip
                    label={campaignsData.length}
                    size="small"
                    color={activeTab === 0 ? "primary" : "default"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Sessions
                  <Chip
                    label={dashboardTilesData?.active_user_sessions || 0}
                    size="small"
                    color={activeTab === 1 ? "primary" : "default"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Tenants
                  <Chip
                    label={tenants.length}
                    size="small"
                    color={activeTab === 2 ? "primary" : "default"}
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Cron Status
                  <Chip
                    label={cronJobsData.length}
                    size="small"
                    color={activeTab === 3 ? "primary" : "default"}
                  />
                </Box>
              }
            />
          </Tabs>
        </Paper>

        {/* Campaigns Tab */}
        {activeTab === 0 && (
          <MarketingCampaignTab tenants={tenants} capitalize={capitalize} convertTimestamp={convertTimestamp} />
        )}

        {/* Sessions Tab */}
        {activeTab === 1 && (
          <ActiveUserSessionsTable
            tenants={tenants}
            capitalize={capitalize}
            convertTimestamp={convertTimestamp}
          />
        )}

        {/* Tenants Tab */}
        {activeTab === 2 && (
          <Paper>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Tenant Summary
              </Typography>
            </Box>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  size="small"
                  label="Filter by Plan"
                  value={planFilter}
                  onChange={(e) => setPlanFilter(e.target.value)}
                  sx={{ minWidth: 500 }}
                >
                  <MenuItem value="all">All Plans</MenuItem>
                  {plans.map((plan) => (
                    <MenuItem key={plan.name} value={plan.name}>
                      {plan.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="none">No Plan</MenuItem>
                </TextField>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredTenants.length} of {tenants.length} tenants
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Tenant Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Plan</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Plan Start Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Plan End Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Active Users</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Current Wallet Balance</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Is Wallet Check Enabled</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Invoice Pending</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTenants.map((tenant) => (
                    <TableRow key={tenant.id} hover>
                      <TableCell>{tenant.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={tenant?.plan?.label || "No Plan"}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                        {tenant?.plan?.status && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {capitalize(tenant.plan.status.replace("_", " "))}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {convertTimestamp(tenant?.plan?.start_date) || "—"}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {convertTimestamp(tenant?.plan?.end_date) || "—"}
                      </TableCell>
                      <TableCell>{tenant?.active_users ?? "—"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {formatBalance(tenant?.wallet_balance, tenant?.currency)}
                      </TableCell>
                      <TableCell>
                        {tenant?.is_wallet_check_enabled === null ||
                        tenant?.is_wallet_check_enabled === undefined
                          ? "—"
                          : tenant.is_wallet_check_enabled
                          ? "True"
                          : "False"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tenant?.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={tenant?.is_active ? "success" : "warning"}
                        />
                      </TableCell>
                      <TableCell>
                        {tenant?.invoice_pending === null ? (
                          "—"
                        ) : (
                          <Chip
                            label={
                              tenant?.invoice_pending ? "Pending" : "Not pending"
                            }
                            size="small"
                            color={tenant?.invoice_pending ? "error" : "success"}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredTenants.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: "divider" }}>
                <CustomPagination
                  count={filteredTenants.length}
                  page={tenantPage}
                  rowsPerPage={tenantRowsPerPage}
                  onPageChange={(event, newPage) => setTenantPage(newPage)}
                  onRowsPerPageChange={(e) => {
                    setTenantRowsPerPage(e.target.value);
                    setTenantPage(0);
                  }}
                />
              </Box>
            )}
            {filteredTenants.length === 0 && (
              <Box sx={{ p: 8, textAlign: "center" }}>
                <ErrorIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No tenants found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Crons Tab */}
        {activeTab === 3 && (
          // <Paper>
          //   <Box
          //     sx={{
          //       p: 2,
          //       bgcolor: "grey.50",
          //       borderBottom: 1,
          //       borderColor: "divider",
          //     }}
          //   >
          //     <Typography variant="h6" fontWeight="bold">
          //       Cron Job Status
          //     </Typography>
          //   </Box>
          //   <Box
          //     sx={{
          //       p: 2,
          //       bgcolor: "grey.50",
          //       borderBottom: 1,
          //       borderColor: "divider",
          //     }}
          //   >
          //     <TextField
          //       fullWidth
          //       size="small"
          //       placeholder="Search cron jobs..."
          //       value={searchQuery}
          //       onChange={(e) => setSearchQuery(e.target.value)}
          //       InputProps={{
          //         startAdornment: (
          //           <InputAdornment position="start">
          //             <SearchIcon />
          //           </InputAdornment>
          //         ),
          //       }}
          //     />
          //     <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          //       Showing {filteredCrons.length} of {cronJobsData.length} cron
          //       jobs
          //     </Typography>
          //   </Box>
          //   <TableContainer>
          //     <Table>
          //       <TableHead>
          //         <TableRow>
          //           <TableCell>
          //             <strong>Cron Name</strong>
          //           </TableCell>
          //           <TableCell>
          //             <strong>Tenant</strong>
          //           </TableCell>
          //           <TableCell>
          //             <strong>Last Ran Time</strong>
          //           </TableCell>
          //           <TableCell>
          //             <strong>Status</strong>
          //           </TableCell>
          //         </TableRow>
          //       </TableHead>
          //       <TableBody>
          //         {filteredCrons.map((cron, cronIndex) => (
          //           <TableRow key={cronIndex} hover>
          //             <TableCell>{cron.cron_name}</TableCell>
          //             <TableCell>
          //               <Typography
          //                 variant="body2"
          //                 sx={{
          //                   fontWeight:
          //                     cron.tenant === "All" ? "bold" : "normal",
          //                   color:
          //                     cron.tenant === "All"
          //                       ? "primary.main"
          //                       : "text.primary",
          //                 }}
          //               >
          //                 {Array.isArray(cron.tenant)
          //                   ? cron.tenant.join(", ")
          //                   : cron.tenant}
          //               </Typography>
          //             </TableCell>
          //             <TableCell>{convertTimestamp(cron?.last_ran_time)}</TableCell>
          //             <TableCell>
          //               <Box
          //                 sx={{ display: "flex", alignItems: "center", gap: 1 }}
          //               >
          //                 {getCronStatusIcon(cron?.status)}
          //                 <Chip
          //                   label={cron.status}
          //                   size="small"
          //                   color={getStatusColor(cron?.status)}
          //                 />
          //               </Box>
          //             </TableCell>
          //           </TableRow>
          //         ))}
          //       </TableBody>
          //     </Table>
          //   </TableContainer>
          //   {filteredCrons.length === 0 && (
          //     <Box sx={{ p: 8, textAlign: "center" }}>
          //       <ErrorIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          //       <Typography variant="h6" color="text.secondary">
          //         No cron jobs found
          //       </Typography>
          //       <Typography variant="body2" color="text.secondary">
          //         Try adjusting your search
          //       </Typography>
          //     </Box>
          //   )}
          // </Paper>
          <CronMonitoringDashboard />
        )}
      </Container>
    </Box>
  );
};

export default AdminMarketingDashboard;
